import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneCall, PhoneOff, History, Voicemail as VoicemailIcon, User, Settings, Mic, MicOff, Wifi, WifiOff } from 'lucide-react';
import { PhoneDialer } from './PhoneDialer';
import { CallHistory } from './CallHistory';
import { VoicemailPlayerEnhanced } from './VoicemailPlayerEnhanced';
import { config } from '@/src/config';
import { useTwilioVoice } from '@/hooks/useTwilioVoice';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import PhoneInput from '@/components/ui/PhoneInput';
interface Member {
  id?: number;
  phone_no?: string;
  phone?: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface MemberPhoneInterfaceProps {
  member: Member;
  enrollmentId?: number;
}

type TabType = 'dialer' | 'contacts' | 'history' | 'voicemail';

export const MemberPhoneInterface: React.FC<MemberPhoneInterfaceProps> = ({ member, enrollmentId }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [activeTab, setActiveTab] = useState<TabType>('dialer');
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [calling, setCalling] = useState(false);
  const [callSid, setCallSid] = useState<string | null>(null);
  const [callStatus, setCallStatus] = useState<string>('');
  const [staffPhone, setStaffPhone] = useState<string>(localStorage.getItem('staff_callback_phone') || '');
  const [callDuration, setCallDuration] = useState<number>(0);
  const [callMode, setCallMode] = useState<'browser' | 'bridge'>(
    localStorage.getItem('call_mode') as 'browser' | 'bridge' || 'browser'
  );
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callHistoryRef = useRef<{ fetchCallHistory?: () => void }>({});

  // Browser-based calling via Twilio Voice SDK
  const {
    deviceReady,
    deviceError,
    callState: browserCallState,
    incomingCall,
    initDevice,
    makeCall: browserMakeCall,
    acceptCall,
    rejectCall,
    hangUp: browserHangUp,
    toggleMute,
    sendDigit,
  } = useTwilioVoice({ autoRegister: callMode === 'browser' });

  // Sync browser call state to local state
  useEffect(() => {
    if (callMode !== 'browser') return;
    if (browserCallState.status === 'idle') {
      if (callSid) {
        // Call just ended — refresh history
        setCallSid(null);
        setCallStatus('');
        setCallDuration(0);
        if (callHistoryRef.current.fetchCallHistory) {
          callHistoryRef.current.fetchCallHistory();
        }
      }
    } else {
      setCallStatus(browserCallState.status);
      setCallDuration(browserCallState.duration);
      if (browserCallState.callSid) setCallSid(browserCallState.callSid);
    }
  }, [browserCallState.status, browserCallState.duration, browserCallState.callSid, callMode]);

  // Poll call status for bridge mode only
  useEffect(() => {
    // 'handoff' calls live in the staff member's own softphone (Zoom Phone),
    // not in Twilio — /api/call-status knows nothing about them, so polling
    // would just churn until it times out.
    if (callMode !== 'bridge' || !callSid || callStatus === 'handoff') return;

    const pollStatus = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await authFetch(`${config.weaveinApi.baseUrl}/api/call-status/${callSid}`);
        const data = await response.json();

        if (data.status && data.status !== 'unknown') {
          setCallStatus(data.status);
          if (data.duration_seconds) setCallDuration(data.duration_seconds);

          // Call ended — stop polling and refresh history
          if (['completed', 'failed', 'canceled', 'busy', 'no-answer'].includes(data.status)) {
            setCallSid(null);
            setCallStatus('');
            setCallDuration(0);
            if (callHistoryRef.current.fetchCallHistory) {
              callHistoryRef.current.fetchCallHistory();
            }
          }
        }
      } catch {
        // Ignore polling errors
      }
    };

    pollIntervalRef.current = setInterval(pollStatus, 3000);
    pollStatus();

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [callSid, callMode, callStatus]);

  // Collect member phone numbers
  const memberContacts = [];
  const primaryPhone = member.phone_no || member.phone || member.phone_number;

  if (primaryPhone) {
    memberContacts.push({
      label: 'Primary Phone',
      number: primaryPhone,
      type: 'mobile'
    });
  }

  const initiateCall = async (phoneNumber: string) => {
    try {
      setCalling(true);
      setCallStatus('initiating');

      // BROWSER MODE: Use Twilio Device to call directly from browser.
      // NEVER silently fall through to bridge mode when the device isn't ready —
      // bridge dials the member from a staff phone, which is surprising and not
      // what the user selected. Surface a clear error and re-register instead.
      if (callMode === 'browser') {
        if (!deviceReady) {
          // Kick a background (re)registration so the status pill recovers.
          void initDevice();
          throw new Error(
            deviceError
              ? `Browser calling isn't ready: ${deviceError}. Wait for "Ready", then retry — or switch to Bridge mode.`
              : 'Browser calling is still connecting. Wait for "Ready", then retry — or switch to Bridge mode.',
          );
        }
        // Normalize phone to E.164 for Twilio
        const digits = phoneNumber.replace(/\D/g, '');
        const e164 = digits.length === 10 ? `+1${digits}` : `+${digits}`;

        await browserMakeCall(e164);
        setCallStatus('connecting');
        setTimeout(() => setActiveTab('history'), 500);
        return;
      }

      // BRIDGE MODE: Call staff phone first, then bridge to member
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (!cboId) {
        throw new Error('Organization context required. Please select an organization.');
      }

      if (!member.id) {
        throw new Error('Member ID is required to make a call.');
      }

      const response = await fetch(`${config.weaveinApi.baseUrl}/api/members/${member.id}/communications/phone`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CBO-ID': cboId
        },
        body: JSON.stringify({
          to_number: phoneNumber,
          enrollment_id: enrollmentId,
          member_id: member.id,
          call_type: enrollmentId ? 'enrollment' : 'general',
          context_message: enrollmentId
            ? `Enrollment call for ${member.first_name} ${member.last_name}`
            : 'General member outreach',
          record: recordingEnabled,
          staff_phone: staffPhone || undefined
        })
      });

      // Handle auth errors specifically
      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }

      if (response.status === 403) {
        throw new Error('You do not have permission to make calls for this member.');
      }

      const result = await response.json();

      if (result.success) {
        setCallSid(result.call_sid);
        if (result.dial_uri) {
          // BYOP providers that cannot originate a call server-side (Zoom Phone)
          // return a dial URI instead. Nothing is ringing yet — the staff
          // member's own softphone places the call when this opens.
          setCallStatus('handoff');
          window.location.href = result.dial_uri;
        } else {
          setCallStatus('ringing');
        }
        // Switch to history tab to show active call
        setTimeout(() => setActiveTab('history'), 500);
      } else {
        throw new Error(result.error || result.message || 'Call failed');
      }

    } catch (error: any) {
      console.error('Call error:', error);
      setCallStatus('failed');
      alert(`Failed to initiate call: ${error.message}`);
    } finally {
      setCalling(false);
    }
  };

  const endCall = () => {
    if (callMode === 'browser') {
      browserHangUp();
    }
    setCallSid(null);
    setCallStatus('');
    setCallDuration(0);
  };

  const formatPhoneNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, '');
    const match = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return number;
  };

  const tabs = [
    { id: 'dialer' as TabType, icon: Phone, label: 'Dialer' },
    { id: 'contacts' as TabType, icon: User, label: 'Contact' },
    { id: 'history' as TabType, icon: History, label: 'Recent' },
    { id: 'voicemail' as TabType, icon: VoicemailIcon, label: 'Voicemail' },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Phone App Container */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden border-8 border-gray-900">
        {/* Status Bar */}
        <div className="bg-gray-900 text-white px-6 py-2 flex items-center justify-between text-xs">
          <span className="font-medium">{t('communications.member_communications', 'Member Communications')}</span>
          <div className="flex items-center gap-3">
            <span>{fmt.time(new Date())}</span>
          </div>
        </div>

        {/* Incoming Call Banner */}
        {incomingCall && (
          <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between animate-pulse">
            <div className="flex items-center gap-3">
              <PhoneCall className="w-5 h-5" />
              <div>
                <p className="font-semibold">
                  {browserCallState.callerInfo?.member_name
                    ? `Incoming: ${browserCallState.callerInfo.member_name}`
                    : 'Incoming Call'}
                </p>
                <p className="text-sm opacity-90">
                  {browserCallState.remoteNumber}
                  {browserCallState.callerInfo?.assigned_name &&
                    ` \u2022 Assigned to: ${browserCallState.callerInfo.assigned_name}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={acceptCall} className="bg-v2accent hover:bg-v2accent/90 px-4 py-2 rounded-full font-semibold">
                Accept
              </button>
              <button onClick={rejectCall} className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-full font-semibold text-xs leading-tight">
                Send to<br/>Voicemail
              </button>
            </div>
          </div>
        )}

        {/* Active Call Banner */}
        {(callSid || (callMode === 'browser' && browserCallState.status !== 'idle')) && (
          <div className={`text-white px-4 py-3 flex items-center justify-between ${
            ['open', 'in_progress'].includes(callStatus) ? 'bg-green-600' :
            callStatus === 'handoff' ? 'bg-blue-600' : 'bg-yellow-600 animate-pulse'
          }`}>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5" />
              <div>
                <p className="font-semibold">
                  {callStatus === 'initiated' || callStatus === 'connecting' ? 'Connecting...' :
                   callStatus === 'ringing' ? 'Ringing...' :
                   callStatus === 'open' || callStatus === 'in_progress' ? 'Call Connected' :
                   callStatus === 'handoff' ? 'Calling from Zoom' :
                   callStatus}
                </p>
                <p className="text-sm opacity-90">
                  {callStatus === 'handoff' ? 'Continue in your Zoom app. The call is logged here when it ends.' :
                   callDuration > 0 ? `Duration: ${Math.floor(callDuration / 60)}:${(callDuration % 60).toString().padStart(2, '0')}` :
                   callSid ? `Call ID: ${callSid.substring(0, 12)}...` : 'Connecting...'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {callMode === 'browser' && ['open'].includes(browserCallState.status) && (
                <button
                  onClick={toggleMute}
                  className={`px-3 py-2 rounded-full font-semibold ${
                    browserCallState.muted ? 'bg-yellow-500' : 'bg-white/20 hover:bg-white/30'
                  }`}
                >
                  {browserCallState.muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              )}
              <button
                onClick={endCall}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-full font-semibold"
              >
                {t('communications.end', 'End')}
              </button>
            </div>
          </div>
        )}

        {/* Settings Bar */}
        <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('communications.call_settings', 'Call Settings')}</span>
            </div>
            <button
              onClick={() => setRecordingEnabled(!recordingEnabled)}
              className={`
                flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium
                transition-all ${
                  recordingEnabled
                    ? 'bg-red-100 text-red-700 border-2 border-red-300'
                    : 'bg-gray-200 text-gray-600 border-2 border-gray-300 dark:border-gray-600'
                }
              `}
            >
              {recordingEnabled ? (
                <>
                  <Mic className="w-4 h-4" />
                  {t('communications.recording_on', 'Recording ON')}
                </>
              ) : (
                <>
                  <MicOff className="w-4 h-4" />
                  {t('communications.recording_off', 'Recording OFF')}
                </>
              )}
            </button>
          </div>

          {/* Call Mode Toggle */}
          <div className="mt-2 flex items-center gap-2">
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              <button
                onClick={() => { setCallMode('browser'); localStorage.setItem('call_mode', 'browser'); }}
                className={`px-3 py-1.5 flex items-center gap-1 ${
                  callMode === 'browser'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 dark:bg-gray-600 dark:text-gray-300'
                }`}
              >
                {deviceReady ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                Browser
              </button>
              <button
                onClick={() => { setCallMode('bridge'); localStorage.setItem('call_mode', 'bridge'); }}
                className={`px-3 py-1.5 flex items-center gap-1 ${
                  callMode === 'bridge'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 dark:bg-gray-600 dark:text-gray-300'
                }`}
              >
                <Phone className="w-3 h-3" />
                Bridge
              </button>
            </div>
            {callMode === 'browser' && (
              <span className={`text-xs ${deviceReady ? 'text-green-600' : 'text-orange-500'}`}>
                {deviceReady ? 'Ready' : deviceError || 'Connecting...'}
              </span>
            )}
          </div>

          {/* Staff Callback Number for Bridge Mode */}
          {callMode === 'bridge' && (
            <div className="mt-2 flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-500" />
              <PhoneInput
                placeholder="Your phone # for live call bridge"
                value={staffPhone}
                onChange={(v) => {
                  setStaffPhone(v);
                  localStorage.setItem('staff_callback_phone', v);
                }}
                className="flex-1 text-sm px-2 py-1.5 border border-gray-300 rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
              />
            </div>
          )}

          {/* Member Info */}
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200 dark:border-blue-700">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-blue-900">
                {member.first_name} {member.last_name}
              </span>
            </div>
            {primaryPhone && (
              <div className="text-sm text-blue-700 mt-1">
                {formatPhoneNumber(primaryPhone)}
              </div>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white min-h-[600px] p-4">
          {activeTab === 'dialer' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 text-center mb-4">
                Call {member.first_name}
              </h2>
              <PhoneDialer
                onCall={initiateCall}
                initialNumber={primaryPhone || ''}
              />
              <div className="text-center text-sm text-gray-500 mt-4">
                {recordingEnabled ? '🔴 Calls will be recorded' : '⚪ Recording disabled'}
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {t('communications.member_contact', 'Member Contact')}
              </h2>

              {memberContacts.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <User className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>{t('communications.no_phone_number_on_file', 'No phone number on file')}</p>
                  <p className="text-sm mt-1">{t('communications.use_the_dialer_to_call', 'Use the dialer to call any number')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {memberContacts.map((contact, idx) => (
                    <button
                      key={idx}
                      onClick={() => initiateCall(contact.number)}
                      disabled={calling}
                      className="
                        w-full p-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl
                        hover:border-blue-400 hover:bg-blue-50 transition-all
                        disabled:opacity-50 disabled:cursor-not-allowed
                        flex items-center justify-between group
                      "
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Phone className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900 dark:text-white">{contact.label}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {formatPhoneNumber(contact.number)}
                          </p>
                          <p className="text-xs text-gray-500 capitalize">{contact.type}</p>
                        </div>
                      </div>
                      <div className="
                        w-10 h-10 bg-green-600 rounded-full flex items-center justify-center
                        group-hover:scale-110 transition-transform
                      ">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-700">
                <p className="text-sm text-gray-700 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>{t('communications.to_call_a_different_number', 'To call a different number, use the')} <strong>{t('communications.dialer', 'Dialer')}</strong> tab</span>
                </p>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <CallHistory
              referralId={enrollmentId?.toString()}
              providerId={member.id?.toString()}
              onRefReady={(ref) => { callHistoryRef.current = ref; }}
            />
          )}

          {activeTab === 'voicemail' && (
            <VoicemailPlayerEnhanced
              referralId={enrollmentId?.toString()}
            />
          )}
        </div>

        {/* Bottom Navigation */}
        <div className="bg-white dark:bg-gray-800 border-t-2 border-gray-200 px-2 py-3 flex items-center justify-around">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex flex-col items-center gap-1 px-4 py-2 rounded-lg
                transition-all ${
                  activeTab === tab.id
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-100'
                }
              `}
            >
              <tab.icon className={`w-6 h-6 ${activeTab === tab.id ? 'scale-110' : ''}`} />
              <span className="text-xs font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Helper Text */}
      <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-300">
        <p>
          {recordingEnabled
            ? '🔴 Call recording is enabled - All calls will be recorded'
            : '⚪ Call recording is disabled - Calls will not be recorded'
          }
        </p>
      </div>
    </div>
  );
};
